import React, { Component, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import { renderableType } from '../proptypes/prop-types';
import { buildClassNames, buildClassNameModifiers } from '../util/utils';

import './nimbusAlert.scss';

const blockBaseClassName = 'alert';
const dismisserBaseClassName = 'alert__dismisser';
const headerBaseClassName = 'alert__header';
const footerBaseClassName = 'alert__footer';

/** A component for displaying alerts to the user */
export class NimbusAlert extends Component {
  static propTypes = {
    /** @ignore */
    children: renderableType,
    /** @ignore */
    className: PropTypes.string,
    /** Indicates whether the alert can be dismissed */
    dismissible: PropTypes.bool,
    /** Content to be rendered at the top-right of the alert, used to dismiss the alert */
    dismissComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    /** Determines which predefined style should be applied to the alert */
    displayClass: PropTypes.oneOf(['default', 'info', 'warn', 'error']),
    /** Content to be rendered at the bottom of the alert */
    footer: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    /** Content to be rendered at the top of the alert */
    header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    /** Function to be called when the alert is dismissed */
    onDismiss: PropTypes.func,
  };

  static defaultProps = {
    dismissible: true,
    displayClass: 'default',
  };

  handleKeyDown = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (event.key === 'Enter') {
      this.props.onDismiss();
    }
  };

  buildDismisser = (dismissible, dismissComponent, onDismiss) => {
    if (dismissible) {
      if (dismissComponent) {
        return (
          cloneElement(dismissComponent, {
            role: dismissComponent.props.role || 'button',
            tabIndex: dismissComponent.props.tabIndex || 0,
            className: buildClassNames(dismissComponent.props.className, dismisserBaseClassName),
            onClick: dismissComponent.props.onClick || onDismiss,
            onKeyDown: dismissComponent.props.onKeyDown
              || (onDismiss ? this.handleKeyDown : undefined),
          })
        );
      }
      return (
        <FontAwesomeIcon
          role='button'
          tabIndex={0}
          icon={faTimesCircle}
          className={dismisserBaseClassName}
          onClick={onDismiss}
          onKeyDown={onDismiss ? this.handleKeyDown : undefined}
        />
      );
    }
    return null;
  };

  render() {
    const {
      children,
      className,
      dismissible,
      dismissComponent,
      displayClass,
      footer,
      header,
      onDismiss,
      ...other
    } = this.props;
    const blockClassNames = buildClassNames(
      className,
      buildClassNameModifiers(blockBaseClassName, displayClass),
      blockBaseClassName,
    );

    const dismisser = this.buildDismisser(dismissible, dismissComponent, onDismiss);

    return (
      <div role='alert' className={blockClassNames} {...other}>
        { header && <div className={headerBaseClassName}>{header}</div>}
        {dismisser}
        {children}
        {footer && <div className={footerBaseClassName}>{footer}</div>}
      </div>
    );
  }
}
